import { useEffect, useRef, useState } from 'react';
import { getSSEServiceUrl } from '../services/clientConfigService';

interface UseAlertSSEOptions {
  seasonYear: number;
  circuitId: string;
  startDate: string;
  onRefreshNeeded: () => void;
  enabled?: boolean;
}

interface UseAlertSSEResult {
  isConnected: boolean;
  connectionError: string | null;
}

/**
 * Hook for connecting to the SSE notification service.
 * 
 * When connected, this hook listens for "refresh" events from the server
 * and calls the onRefreshNeeded callback when received.
 * 
 * Features:
 * - Automatic reconnection with exponential backoff
 * - Debounced callback to prevent rapid refreshes
 * - Graceful handling of connection errors
 */
export function useAlertSSE({
  seasonYear,
  circuitId,
  startDate,
  onRefreshNeeded,
  enabled = true,
}: UseAlertSSEOptions): UseAlertSSEResult {
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  
  // Store callback in ref to avoid dependency changes
  const onRefreshNeededRef = useRef(onRefreshNeeded);
  onRefreshNeededRef.current = onRefreshNeeded;
  
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    
    // Get SSE service URL from config (supports ENV override)
    const sseServiceUrl = getSSEServiceUrl();
    
    // Skip if SSE is not configured or not enabled
    if (!sseServiceUrl || !enabled) {
      return;
    }

    // Skip if missing required parameters
    if (!seasonYear || !circuitId || !startDate) {
      return;
    }

    // Clean up any existing connection first
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Reset reconnect attempts
    reconnectAttemptsRef.current = 0;

    const connect = () => {
      // Don't connect if unmounted
      if (!mountedRef.current) return;

      // Build SSE URL
      const url = new URL('/stream', sseServiceUrl);
      url.searchParams.set('season_year', seasonYear.toString());
      url.searchParams.set('circuit_id', circuitId);
      url.searchParams.set('start_date', startDate);

      try {
        const eventSource = new EventSource(url.toString());
        eventSourceRef.current = eventSource;

        eventSource.onopen = () => {
          if (!mountedRef.current) {
            eventSource.close();
            return;
          }
          setIsConnected(true);
          setConnectionError(null);
          reconnectAttemptsRef.current = 0;
        };

        // Handle named "connected" event
        eventSource.addEventListener('connected', (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('[SSE] Connection confirmed:', data);
          } catch {
            // Ignore parse errors
          }
        });

        // Handle named "refresh" event (our notification)
        eventSource.addEventListener('refresh', (event) => {
          if (!mountedRef.current) return;
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'refresh_alerts') {
              console.log('[SSE] Received refresh signal:', data);
              // Debounce the refresh call
              if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
              }
              debounceTimeoutRef.current = setTimeout(() => {
                if (mountedRef.current) {
                  onRefreshNeededRef.current();
                }
              }, 500);
            }
          } catch {
            // Ignore parse errors
          }
        });

        // Handle generic messages (fallback)
        eventSource.onmessage = (event) => {
          if (!mountedRef.current) return;
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'refresh_alerts') {
              console.log('[SSE] Received refresh signal (onmessage):', data);
              if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
              }
              debounceTimeoutRef.current = setTimeout(() => {
                if (mountedRef.current) {
                  onRefreshNeededRef.current();
                }
              }, 500);
            }
          } catch {
            // Ignore parse errors
          }
        };

        eventSource.onerror = () => {
          if (!mountedRef.current) {
            eventSource.close();
            return;
          }
          
          setIsConnected(false);
          eventSource.close();
          eventSourceRef.current = null;

          // Exponential backoff reconnection (max 30 seconds)
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          reconnectAttemptsRef.current++;
          
          if (reconnectAttemptsRef.current <= 10 && mountedRef.current) {
            console.log(`[SSE] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);
            setConnectionError(`Reconnecting... (attempt ${reconnectAttemptsRef.current})`);
            
            reconnectTimeoutRef.current = setTimeout(connect, delay);
          } else if (mountedRef.current) {
            setConnectionError('SSE connection failed after multiple attempts');
            console.error('[SSE] Max reconnection attempts reached');
          }
        };
      } catch (error) {
        if (mountedRef.current) {
          setConnectionError('Failed to create SSE connection');
          console.error('[SSE] Failed to create connection:', error);
        }
      }
    };

    connect();

    // Cleanup on unmount or dependency change
    return () => {
      mountedRef.current = false;
      
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      
      setIsConnected(false);
    };
  }, [seasonYear, circuitId, startDate, enabled]); // Removed callback from deps

  return {
    isConnected,
    connectionError,
  };
}

/**
 * Check if SSE is configured (URL is set)
 */
export function isSSEConfigured(): boolean {
  return !!getSSEServiceUrl();
}
